import { useState, useEffect } from 'react';
import _ from 'lodash';

export interface FieldItem {
  required?: boolean;
  max?: number;
  min?: number;
  patterns?: Array<{
    pattern: RegExp;
    message: string;
  }>;
  rules?: Array<(data: any) => string>;
}

export interface UseFormEditProps {
  data: any;
  config: Record<string, FieldItem>;
}

export interface FormEditProps {
  formData: Record<string, any>;
  formError: Record<string, string>;
  onChange: (e: any, name: string, value: any) => void;
}

const checkField = (config: FieldItem, value: any, data: any) => {
  const { required, max, min, patterns = [], rules = [] } = config;
  if (required && !value && value !== 0) {
    return '不能为空';
  }

  if (max && value?.length > max) {
    return '长度过长';
  }

  if (min && value?.length < min) {
    return '长度过短';
  }

  let patternError = '';
  patterns.some(({ pattern, message }) => {
    if (!pattern.test(value)) {
      patternError = message;
      return true;
    }
    return false;
  });
  if (patternError) {
    return patternError;
  }

  let ruleError = '';
  rules.some((rule) => {
    ruleError = rule(data);
    return !!ruleError.length;
  });
  return ruleError;
};

const useFormEdit = (props: UseFormEditProps) => {
  const { config, data } = props;
  const [formData, setFormData] = useState<Record<string, any>>({ ...data });
  const [formError, setFormError] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!data || _.isEmpty(data)) {
      return;
    }
    setFormData({ ...data });
  }, [data]);

  const onChange = (e: any, name: string, value: any) => {
    const newData = {
      ...formData,
      [name]: value,
    };
    setFormData(newData);
    setFormError({
      ...formError,
      [name]: checkField(config[name] || {}, value, newData),
    });
  };

  const onBatchChange = (e: any, data: Record<string, any>) => {
    const newData = {
      ...formData,
      ...data,
    };
    setFormData(newData);
    let valid = true;
    const formError = Object.keys(data).reduce((map, key) => {
      const fieldError = checkField(config[key] || {}, data[key], newData);
      valid = valid && !fieldError.length;
      return { ...map, [key]: fieldError };
    }, {});
    setFormError(formError);
  };

  const validate = () => {
    let valid = true;
    const formError = Object.keys(config).reduce((map, key) => {
      const fieldError = checkField(config[key] || {}, formData[key], formData);
      valid = valid && !fieldError.length;
      return { ...map, [key]: fieldError };
    }, {});
    setFormError(formError);
    return valid;
  };

  const onSubmit = () => {
    if (!validate()) return;
    return formData;
  };

  return {
    formData,
    formError,
    onChange,
    onBatchChange,
    onSubmit,
  };
};

export default useFormEdit;
